import '@aws-amplify/ui-react/styles.css';
import { usePageTracking } from 'content/hooks';
import { lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Status404 from '../../content/pages/Status/Status404';
import { BaseLayout, SidebarLayout } from '../../layouts';
import { createAmsPartsRoutes } from './ams-parts.routes';
import { createBannersRoutes } from './banners.routes';
import { createBarsRoutes } from './bars.routes';
import { createBrewersRoutes } from './brewers.routes';
import { HomePageRedirect, RequireAuth } from './Components';
import { createClientsRoutes } from './clients.routes';
import { createDashboardRoutes } from './dashboard.routes';
import { createHelpArticlesRoutes } from './help-articles.routes';
import { createLegalsRoutes } from './legals.routes';
import { Loader } from './loader.routes';
import { createLogsRoutes } from './logs.routes';
import { createProductsRoutes } from './products.routes';
import { createRefMaterialsRoutes } from './ref-materials.routes';
import { createReportRoutes } from './reports.routes';
import { createTspRoutes } from './technical-support-providers.routes';
import { createUsersRoutes } from './users.routes';
import { createVenuesRoutes } from './venues.routes';
import { createVisitsRoutes } from './visits.routes';

function MainRoutes() {
  return (
    <BrowserRouter>
      <TrackedRoutes />
    </BrowserRouter>
  );
}

function TrackedRoutes() {
  // Google Analytics SPA page tracking
  usePageTracking();

  const SignIn = Loader(lazy(() => import('../../content/pages/Auth/SignIn')));

  const ForceNewPassword = Loader(lazy(() => import('../../content/pages/Auth/ForceNewPassword')));
  const ForgotPassword = Loader(lazy(() => import('../../content/pages/Auth/ForgotPassword')));
  const PasswordReset = Loader(lazy(() => import('../../content/pages/Auth/PasswordReset')));
  const TotpSetup = Loader(lazy(() => import('../../content/pages/Auth/TotpSetup')));
  const TotpVerify = Loader(lazy(() => import('../../content/pages/Auth/TotpVerify')));

  const usersRoutes = createUsersRoutes();
  const brewersRoutes = createBrewersRoutes();
  const amsPartsRoutes = createAmsPartsRoutes();
  const clientsRoutes = createClientsRoutes();
  const venuesRoutes = createVenuesRoutes();
  const dashboardRoutes = createDashboardRoutes();
  const helpArticlesRoutes = createHelpArticlesRoutes();
  const productsRoutes = createProductsRoutes();
  const barsRoutes = createBarsRoutes();
  const visitsRoutes = createVisitsRoutes();
  const tspRoutes = createTspRoutes();
  const legalRoutes = createLegalsRoutes();
  const refMaterialsRoutes = createRefMaterialsRoutes();
  const reportRoutes = createReportRoutes();
  const bannersRoutes = createBannersRoutes();
  const logsRoutes = createLogsRoutes();

  return (
    <Routes>
      <Route
        path="/signin"
        element={
          <BaseLayout>
            <SignIn />
          </BaseLayout>
        }
      />
      <Route
        path="/force-password"
        element={
          <BaseLayout>
            <ForceNewPassword />
          </BaseLayout>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <BaseLayout>
            <ForgotPassword />
          </BaseLayout>
        }
      />
      <Route
        path="/password-reset"
        element={
          <BaseLayout>
            <PasswordReset />
          </BaseLayout>
        }
      />
      <Route
        path="/totp-setup"
        element={
          <BaseLayout>
            <TotpSetup />
          </BaseLayout>
        }
      />
      <Route
        path="/totp-verify"
        element={
          <BaseLayout>
            <TotpVerify />
          </BaseLayout>
        }
      />
      <Route
        path="/"
        element={
          <>
            <SidebarLayout />
            <RequireAuth>
              <HomePageRedirect />
            </RequireAuth>
          </>
        }
      />
      <Route
        path="*"
        element={
          <RequireAuth>
            <BaseLayout>
              <Status404 />
            </BaseLayout>
          </RequireAuth>
        }
      />
      <Route path="/users" element={<SidebarLayout />}>
        {usersRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/brewers" element={<SidebarLayout />}>
        {brewersRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/ams" element={<SidebarLayout />}>
        {amsPartsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/clients" element={<SidebarLayout />}>
        {clientsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/venues" element={<SidebarLayout />}>
        {venuesRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/dashboard" element={<SidebarLayout />}>
        {dashboardRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/products" element={<SidebarLayout />}>
        {productsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/ref-materials" element={<SidebarLayout />}>
        {refMaterialsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/bars" element={<SidebarLayout />}>
        {barsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/visits" element={<SidebarLayout />}>
        {visitsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/technical-support-providers" element={<SidebarLayout />}>
        {tspRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/settings/legals" element={<SidebarLayout />}>
        {legalRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/reports" element={<SidebarLayout />}>
        {reportRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/help-articles" element={<SidebarLayout />}>
        {helpArticlesRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/banners" element={<SidebarLayout />}>
        {bannersRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      <Route path="/logs" element={<SidebarLayout />}>
        {logsRoutes.children?.map((childRoute) => (
          <Route
            key={childRoute.path}
            path={`${childRoute.path}`}
            element={<RequireAuth>{childRoute.element}</RequireAuth>}
          />
        ))}
      </Route>
      {/* TODO: 400 */}
      {/* <Route path='400' element={<BaseLayout><Status400 /></BaseLayout >} /> */}
      <Route
        path="*"
        element={
          <BaseLayout>
            <SignIn />
          </BaseLayout>
        }
      />
    </Routes>
  );
}

export default MainRoutes;
